/**
 * Person title definitions for the `personne` target category.
 *
 * Each entry defines a canonical code (stored on the Target), an emoji, a category,
 * and per-language short + long forms. Short forms are used as signal prefixes
 * (e.g., "Dr"); long forms generate additional variants (e.g., "Docteur").
 *
 * Signal generation: for each language's short/long form × (lastName, firstName+lastName,
 * initial+lastName). All language variants are offered as checkboxes so cross-language
 * mentions (a French doctor cited in English) are covered.
 *
 * Search in the title picker uses the brand's working language: the UI shows the working-
 * language label, but once selected, all language forms generate checkboxes.
 *
 * Rule (mirrors D29): adding a form = one row here + no compiler change (title/firstName/
 * lastName are `givenname`/`titlecode` types that produce no direct signal; signals come
 * entirely from the `alias` list the frontend builds and saves).
 */

export type PersonTitleCategory =
  | 'civilite'
  | 'professionnel'
  | 'militaire'
  | 'religieux'
  | 'noblesse'
  | 'politique'
  | 'autre';

export interface PersonTitleDef {
  code: string;
  emoji: string;
  category: PersonTitleCategory;
  /**
   * Per ISO-639-1 language code. `short` = prefix forms ("Dr"), `long` = expanded forms
   * ("Docteur"). Both produce signal variants. Absent `long` means short only.
   */
  forms: Partial<Record<string, { short: string[]; long?: string[] }>>;
}

/** Language display names and flags for grouping checkboxes in the form. */
export const PERSON_TITLE_LANG_LABELS: Record<string, string> = {
  fr: '🇫🇷 Français',
  en: '🇬🇧 English',
  de: '🇩🇪 Deutsch',
  es: '🇪🇸 Español',
  it: '🇮🇹 Italiano',
  pt: '🇵🇹 Português',
  nl: '🇳🇱 Nederlands',
  ar: '🌙 العربية',
  fa: '🌙 فارسی',
  he: '✡️ עברית',
  ja: '🎌 日本語',
  zh: '🀄 中文',
  ms: '🌴 Bahasa Melayu',
};

export const PERSON_TITLES: PersonTitleDef[] = [
  // ─── Civilités ───────────────────────────────────────────────────────────

  {
    code: 'M',
    emoji: '👨',
    category: 'civilite',
    forms: {
      fr: { short: ['M.'],       long: ['Monsieur'] },
      en: { short: ['Mr', 'Mr.'], long: ['Mister'] },
      de: { short: ['Hr', 'Hr.'], long: ['Herr'] },
      es: { short: ['Sr', 'Sr.'], long: ['Señor'] },
      it: { short: ['Sig.'],     long: ['Signore'] },
      pt: { short: ['Sr', 'Sr.'], long: ['Senhor'] },
      nl: { short: ['Dhr.'],     long: ['Meneer', 'De heer'] },
    },
  },
  {
    code: 'Mme',
    emoji: '👩',
    category: 'civilite',
    forms: {
      fr: { short: ['Mme'],          long: ['Madame'] },
      en: { short: ['Mrs', 'Ms', 'Mx'], long: ['Missus', 'Madam'] },
      de: { short: ['Fr.'],          long: ['Frau'] },
      es: { short: ['Sra', 'Sra.'],  long: ['Señora'] },
      it: { short: ['Sig.ra'],       long: ['Signora'] },
      pt: { short: ['Sra', 'Sra.'],  long: ['Senhora'] },
      nl: { short: ['Mevr.'],        long: ['Mevrouw'] },
    },
  },
  {
    code: 'Mlle',
    emoji: '👩',
    category: 'civilite',
    forms: {
      fr: { short: ['Mlle'],        long: ['Mademoiselle'] },
      en: { short: ['Miss'] },
      es: { short: ['Srta', 'Srta.'], long: ['Señorita'] },
      it: { short: ['Sig.na'],      long: ['Signorina'] },
      pt: { short: ['Srta', 'Srta.'], long: ['Senhorita'] },
    },
  },
  {
    code: 'Aqa',
    emoji: '👨',
    category: 'civilite',
    forms: {
      fa: { short: ['آقای', 'āqā'] },
    },
  },
  {
    code: 'Khanom',
    emoji: '👩',
    category: 'civilite',
    forms: {
      fa: { short: ['خانم', 'khānom'] },
    },
  },
  {
    code: 'Sidi',
    emoji: '👳',
    category: 'civilite',
    forms: {
      ar: { short: ['سيدي', 'Sidi'] },
      fr: { short: ['Sidi'] },
    },
  },
  {
    code: 'Lalla',
    emoji: '👩',
    category: 'civilite',
    forms: {
      ar: { short: ['للا', 'Lalla'] },
      fr: { short: ['Lalla'] },
    },
  },
  {
    code: 'Moulay',
    emoji: '👳',
    category: 'civilite',
    forms: {
      ar: { short: ['مولاي', 'Moulay'] },
      fr: { short: ['Moulay'] },
    },
  },
  {
    code: 'san',
    emoji: '🎌',
    category: 'civilite',
    forms: {
      ja: { short: ['-san', 'さん'] },
    },
  },
  {
    code: 'sensei',
    emoji: '🎌',
    category: 'civilite',
    forms: {
      ja: { short: ['-sensei', '先生'] },
    },
  },
  {
    code: 'sama',
    emoji: '🎌',
    category: 'civilite',
    forms: {
      ja: { short: ['-sama', '様'] },
    },
  },
  {
    code: 'laoshi',
    emoji: '🎌',
    category: 'civilite',
    forms: {
      zh: { short: ['老师'] },
    },
  },
  {
    code: 'xiansheng',
    emoji: '🎌',
    category: 'civilite',
    forms: {
      zh: { short: ['先生'] },
    },
  },

  // ─── Académiques / professionnels ────────────────────────────────────────

  {
    code: 'Dr',
    emoji: '🩺',
    category: 'professionnel',
    forms: {
      fr: { short: ['Dr'],              long: ['Docteur', 'Docteure'] },
      en: { short: ['Dr', 'Dr.'],       long: ['Doctor'] },
      de: { short: ['Dr', 'Dr.'],       long: ['Doktor', 'Doktorin'] },
      es: { short: ['Dr', 'Dr.', 'Dra', 'Dra.'], long: ['Doctor', 'Doctora'] },
      it: { short: ['Dott.', 'Dott.ssa'], long: ['Dottore', 'Dottoressa'] },
      pt: { short: ['Dr', 'Dr.', 'Dra', 'Dra.'], long: ['Doutor', 'Doutora'] },
      nl: { short: ['Dr', 'Dr.'],       long: ['Dokter'] },
    },
  },
  {
    code: 'Pr',
    emoji: '🎓',
    category: 'professionnel',
    forms: {
      fr: { short: ['Pr', 'Prof.'],       long: ['Professeur', 'Professeure'] },
      en: { short: ['Prof', 'Prof.'],     long: ['Professor'] },
      de: { short: ['Prof', 'Prof.'],     long: ['Professor', 'Professorin'] },
      es: { short: ['Prof', 'Prof.'],     long: ['Profesor', 'Profesora'] },
      it: { short: ['Prof.', 'Prof.ssa'], long: ['Professore', 'Professoressa'] },
      pt: { short: ['Prof', 'Prof.', 'Profa.'], long: ['Professor', 'Professora'] },
      nl: { short: ['Prof', 'Prof.'],     long: ['Professor'] },
    },
  },
  {
    code: 'Me',
    emoji: '⚖️',
    category: 'professionnel',
    forms: {
      fr: { short: ['Me'],        long: ['Maître', 'Maîtresse'] },
      en: { short: ['Esq.'],      long: ['Esquire'] },
      nl: { short: ['Mr', 'Mr.'], long: ['Meester'] },
    },
  },
  {
    code: 'Ing',
    emoji: '⚙️',
    category: 'professionnel',
    forms: {
      fr: { short: ['Ing.'], long: ['Ingénieur', 'Ingénieure'] },
      en: { short: ['Eng.'], long: ['Engineer'] },
      de: { short: ['Ing.'], long: ['Ingenieur', 'Ingenieurin'] },
      es: { short: ['Ing.'], long: ['Ingeniero', 'Ingeniera'] },
      it: { short: ['Ing.'], long: ['Ingegnere'] },
      pt: { short: ['Eng.'], long: ['Engenheiro', 'Engenheira'] },
    },
  },
  {
    code: 'Ostad',
    emoji: '📚',
    category: 'professionnel',
    forms: {
      fa: { short: ['استاد', 'Ostād', 'Ustad'] },
    },
  },
  {
    code: 'Mohandes',
    emoji: '⚙️',
    category: 'professionnel',
    forms: {
      fa: { short: ['مهندس', 'Mohandes'] },
    },
  },
  {
    code: 'HojjatAlIslam',
    emoji: '🕌',
    category: 'professionnel',
    forms: {
      ar: { short: ['حجة الإسلام', 'Hojjat al-Islam'] },
      fa: { short: ['حجت‌الاسلام', 'Hojjatoleslam'] },
    },
  },
  {
    code: 'Ayatollah',
    emoji: '🕌',
    category: 'professionnel',
    forms: {
      ar: { short: ['آية الله', 'Ayatollah'] },
      fa: { short: ['آیت‌الله', 'Ayatollah'] },
      fr: { short: ['Ayatollah'] },
      en: { short: ['Ayatollah'] },
    },
  },

  // ─── Militaires ──────────────────────────────────────────────────────────

  {
    code: 'General',
    emoji: '🎖️',
    category: 'militaire',
    forms: {
      fr: { short: ['Gén.'], long: ['Général', 'Générale'] },
      en: { short: ['Gen.'], long: ['General'] },
      de: { short: ['Gen.'], long: ['General', 'Generalin'] },
      es: { short: ['Gral.'], long: ['General'] },
      it: { short: ['Gen.'], long: ['Generale'] },
    },
  },
  {
    code: 'Amiral',
    emoji: '🎖️',
    category: 'militaire',
    forms: {
      fr: { short: ['Ami.'], long: ['Amiral'] },
      en: { short: ['Adm.'], long: ['Admiral'] },
      de: { short: ['Adm.'], long: ['Admiral'] },
      es: { short: ['Almte.'], long: ['Almirante'] },
      it: { short: ['Amm.'], long: ['Ammiraglio'] },
    },
  },
  {
    code: 'Colonel',
    emoji: '🎖️',
    category: 'militaire',
    forms: {
      fr: { short: ['Col.'], long: ['Colonel', 'Colonelle'] },
      en: { short: ['Col.'], long: ['Colonel'] },
      de: { short: ['Oberst'] },
      es: { short: ['Cnel.'], long: ['Coronel'] },
      it: { short: ['Col.'], long: ['Colonnello'] },
    },
  },
  {
    code: 'Major',
    emoji: '🎖️',
    category: 'militaire',
    forms: {
      fr: { short: ['Maj.'], long: ['Major'] },
      en: { short: ['Maj.'], long: ['Major'] },
      de: { short: ['Maj.'], long: ['Major'] },
      es: { short: ['Cdte.'], long: ['Comandante', 'Mayor'] },
      it: { short: ['Magg.'], long: ['Maggiore'] },
    },
  },
  {
    code: 'Lieutenant',
    emoji: '🎖️',
    category: 'militaire',
    forms: {
      fr: { short: ['Lt', 'Lt.'], long: ['Lieutenant', 'Lieutenante'] },
      en: { short: ['Lt', 'Lt.'], long: ['Lieutenant'] },
      de: { short: ['Lt.'], long: ['Leutnant'] },
      es: { short: ['Tte.'], long: ['Teniente'] },
      it: { short: ['Ten.'], long: ['Tenente'] },
    },
  },
  {
    code: 'Capitaine',
    emoji: '🚢',
    category: 'militaire',
    forms: {
      fr: { short: ['Cpt.'], long: ['Capitaine'] },
      en: { short: ['Capt.'], long: ['Captain'] },
      de: { short: ['Kapt.'], long: ['Kapitän', 'Kapitänin'] },
      es: { short: ['Cap.'], long: ['Capitán', 'Capitana'] },
      it: { short: ['Cap.'], long: ['Capitano'] },
    },
  },
  {
    code: 'Sergent',
    emoji: '🪖',
    category: 'militaire',
    forms: {
      fr: { short: ['Sgt.'], long: ['Sergent', 'Sergente'] },
      en: { short: ['Sgt.'], long: ['Sergeant'] },
      de: { short: ['Sgt.'], long: ['Feldwebel'] },
      es: { short: ['Sgto.'], long: ['Sargento'] },
      it: { short: ['Sgt.'], long: ['Sergente'] },
    },
  },

  // ─── Religieux ───────────────────────────────────────────────────────────

  {
    code: 'Pere',
    emoji: '⛪',
    category: 'religieux',
    forms: {
      fr: { short: ['P.', 'Abbé'], long: ['Père', 'Révérend'] },
      en: { short: ['Fr', 'Rev.'], long: ['Father', 'Reverend', 'Abbot'] },
      de: { short: ['P.', 'Pf.'], long: ['Pater', 'Pastor'] },
      es: { short: ['P.', 'Fr', 'Rvdo.'], long: ['Padre', 'Reverendo'] },
      it: { short: ['P.', 'Fr', 'Rev.'], long: ['Padre', 'Reverendo'] },
    },
  },
  {
    code: 'Monseigneur',
    emoji: '🧣',
    category: 'religieux',
    forms: {
      fr: { short: ['Mgr'], long: ['Monseigneur', 'Cardinal'] },
      en: { short: ['Bp', 'Abp', 'Card.'], long: ['Bishop', 'Archbishop', 'Cardinal'] },
      es: { short: ['Mons.', 'Card.'], long: ['Monseñor', 'Cardenal'] },
      it: { short: ['Mons.', 'Card.'], long: ['Monsignore', 'Cardinale'] },
    },
  },
  {
    code: 'Soeur',
    emoji: '⛪',
    category: 'religieux',
    forms: {
      fr: { short: ['Sr', 'Fr'], long: ['Sœur', 'Frère'] },
      en: { short: ['Sr', 'Br'], long: ['Sister', 'Brother'] },
      es: { short: ['Sor', 'Hno', 'Hna'], long: ['Hermano', 'Hermana'] },
      it: { short: ['Sr', 'Fr'], long: ['Suora', 'Fratello'] },
    },
  },
  {
    code: 'Imam',
    emoji: '🕌',
    category: 'religieux',
    forms: {
      ar: { short: ['إمام', 'Imam', 'Sheikh', 'شيخ'] },
      fr: { short: ['Imam', 'Sheikh', 'Hazrat'] },
      en: { short: ['Imam', 'Sheikh', 'Hazrat'] },
    },
  },
  {
    code: 'Rabbi',
    emoji: '🕎',
    category: 'religieux',
    forms: {
      fr: { short: ['Rav.', 'Rab.'], long: ['Rabbin', 'Rabbi'] },
      en: { short: ['Rabbi', 'Rab.'] },
      he: { short: ['רב', 'הרב'] },
    },
  },
  {
    code: 'Hajji',
    emoji: '🕋',
    category: 'religieux',
    forms: {
      ar: { short: ['حاجّ', 'Hajji'] },
      fr: { short: ['Hajj', 'Hadj'] },
      en: { short: ['Hajji', 'Haj'] },
    },
  },

  // ─── Noblesse / distinction ───────────────────────────────────────────────

  {
    code: 'Sir',
    emoji: '🏇',
    category: 'noblesse',
    forms: {
      en: { short: ['Sir'] },
      fr: { short: ['Sir'] },
    },
  },
  {
    code: 'Dame',
    emoji: '🏇',
    category: 'noblesse',
    forms: {
      en: { short: ['Dame'] },
      fr: { short: ['Dame'] },
    },
  },
  {
    code: 'Lord',
    emoji: '👑',
    category: 'noblesse',
    forms: {
      en: { short: ['Lord', 'Lady'] },
      fr: { short: ['Lord', 'Lady'] },
    },
  },
  {
    code: 'Duc',
    emoji: '👑',
    category: 'noblesse',
    forms: {
      fr: { short: ['Duc', 'Duchesse'] },
      en: { short: ['Duke', 'Duchess'] },
      es: { short: ['Duque', 'Duquesa'] },
      it: { short: ['Duca', 'Duchessa'] },
      de: { short: ['Herzog', 'Herzogin'] },
    },
  },
  {
    code: 'Comte',
    emoji: '👑',
    category: 'noblesse',
    forms: {
      fr: { short: ['Cte'], long: ['Comte', 'Comtesse'] },
      en: { short: ['Earl', 'Count', 'Countess'] },
      es: { short: ['Conde', 'Condesa'] },
      it: { short: ['Conte', 'Contessa'] },
      de: { short: ['Graf', 'Gräfin'] },
    },
  },
  {
    code: 'Baron',
    emoji: '👑',
    category: 'noblesse',
    forms: {
      fr: { short: ['Baron', 'Baronne'] },
      en: { short: ['Baron', 'Baroness'] },
      de: { short: ['Freiherr', 'Freifrau'] },
      es: { short: ['Barón', 'Baronesa'] },
      it: { short: ['Barone', 'Baronessa'] },
    },
  },
  {
    code: 'Prince',
    emoji: '👑',
    category: 'noblesse',
    forms: {
      fr: { short: ['Prince', 'Princesse'] },
      en: { short: ['Prince', 'Princess'] },
      es: { short: ['Príncipe', 'Princesa'] },
      it: { short: ['Principe', 'Principessa'] },
      de: { short: ['Prinz', 'Prinzessin'] },
      ar: { short: ['الأمير', 'الأميرة'] },
    },
  },
  {
    code: 'Tengku',
    emoji: '👑',
    category: 'noblesse',
    forms: {
      ms: { short: ['Tengku', 'Tunku'] },
    },
  },

  // ─── Politique / judiciaire ───────────────────────────────────────────────

  {
    code: 'President',
    emoji: '🏛️',
    category: 'politique',
    forms: {
      fr: { short: ['Pdt.'], long: ['Président', 'Présidente'] },
      en: { short: ['Pres.'], long: ['President'] },
      de: { short: ['Präs.'], long: ['Präsident', 'Präsidentin'] },
      es: { short: ['Pdte.', 'Pdta.'], long: ['Presidente', 'Presidenta'] },
      it: { short: ['Pres.'], long: ['Presidente'] },
    },
  },
  {
    code: 'Ambassadeur',
    emoji: '📜',
    category: 'politique',
    forms: {
      fr: { short: ['Amb.'], long: ['Ambassadeur', 'Ambassadrice'] },
      en: { short: ['Amb.'], long: ['Ambassador'] },
      de: { short: ['Bot.'], long: ['Botschafter', 'Botschafterin'] },
      es: { short: ['Emb.'], long: ['Embajador', 'Embajadora'] },
      it: { short: ['Amb.'], long: ['Ambasciatore', 'Ambasciatrice'] },
    },
  },
  {
    code: 'Juge',
    emoji: '⚖️',
    category: 'politique',
    forms: {
      fr: { short: ['J.'], long: ['Juge'] },
      en: { short: ['J.', 'Hon.'], long: ['Judge', 'Justice', 'The Honorable'] },
      de: { short: ['Ri.'], long: ['Richter', 'Richterin'] },
      es: { short: ['Juez', 'Jueza'] },
      it: { short: ['Giud.'], long: ['Giudice'] },
    },
  },
  {
    code: 'Sheriff',
    emoji: '🤠',
    category: 'politique',
    forms: {
      en: { short: ['Sheriff'] },
      fr: { short: ['Sheriff', 'Shérif'] },
    },
  },
  {
    code: 'Senateur',
    emoji: '🏛️',
    category: 'politique',
    forms: {
      fr: { short: ['Sén.', 'Dép.'], long: ['Sénateur', 'Sénatrice', 'Député', 'Députée'] },
      en: { short: ['Sen.', 'Rep.'], long: ['Senator', 'Representative'] },
      de: { short: ['Abg.'], long: ['Abgeordneter', 'Abgeordnete', 'Senator', 'Senatorin'] },
      es: { short: ['Sen.', 'Dip.'], long: ['Senador', 'Senadora', 'Diputado', 'Diputada'] },
      it: { short: ['Sen.', 'On.'], long: ['Senatore', 'Senatrice', 'Onorevole'] },
    },
  },

  // ─── Autres courants ─────────────────────────────────────────────────────

  {
    code: 'Coach',
    emoji: '🏋️',
    category: 'autre',
    forms: {
      fr: { short: ['Coach'] },
      en: { short: ['Coach'] },
      de: { short: ['Coach'] },
      es: { short: ['Coach', 'Entrenador', 'Entrenadora'] },
      it: { short: ['Coach', 'Allenatore', 'Allenatrice'] },
    },
  },
  {
    code: 'Chef',
    emoji: '💼',
    category: 'autre',
    forms: {
      fr: { short: ['Chef', 'Boss'] },
      en: { short: ['Boss', 'Chief', 'Head'] },
      de: { short: ['Chef', 'Chefin'] },
    },
  },
  {
    code: 'Doc',
    emoji: '🩺',
    category: 'autre',
    forms: {
      fr: { short: ['Doc'] },
      en: { short: ['Doc'] },
    },
  },
];

/** Look up a title by its code. */
export function getPersonTitleDef(code: string): PersonTitleDef | undefined {
  return PERSON_TITLES.find((t) => t.code === code);
}

/**
 * One generated name form for the form's checkbox list.
 *
 * - `lang`: ISO-639-1 language code, or `''` for the "sans titre" group.
 * - `defaultOn`: whether the checkbox starts checked. Title-specific forms are
 *   `false` by default — they are opt-in so the user can filter. Sans-titre forms
 *   are `true`, except the bare last name which is too generic.
 * - `core`: for title-specific forms only — the corresponding no-title form (the
 *   form that becomes redundant when this titled form is checked). Checking
 *   `"Dr de Place"` auto-unchecks `"de Place"` (its core).
 */
export interface PersonNameForm {
  lang: string;
  form: string;
  defaultOn: boolean;
  core?: string; // only on titled forms — the sans-titre equivalent to auto-uncheck
}

/**
 * Generate all name forms for a person.
 *
 * Title-specific forms: `defaultOn=false` — opt-in, shown in a collapsed section.
 * Sans-titre forms: `defaultOn=true` except the bare lastName (too generic).
 */
export function generatePersonNames(
  title: PersonTitleDef | null,
  firstName: string,
  lastName: string,
): PersonNameForm[] {
  const fn = firstName.trim();
  const ln = lastName.trim();
  if (!ln) return [];

  // No period after the initial: the matcher's _tokenize strips trailing punctuation
  // from tokens ("A." → "a"), so the stored signal "dr a. levy" never matches the
  // probe "dr a levy". Storing "Dr A Lévy" (no period) aligns both sides.
  const initial = fn ? fn.charAt(0).toUpperCase() : null;
  const seen = new Set<string>();
  const out: PersonNameForm[] = [];

  const push = (lang: string, form: string, defaultOn: boolean, core?: string) => {
    const key = form.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ lang, form, defaultOn, core });
  };

  if (title) {
    for (const [lang, langForms] of Object.entries(title.forms)) {
      if (!langForms) continue;
      const allPrefixes = [
        ...langForms.short,
        ...(langForms.long ?? []),
      ];
      for (const prefix of allPrefixes) {
        // Title-specific forms start unchecked (defaultOn: false).
        // Each carries its `core` — the corresponding no-title form that becomes
        // redundant once this more-specific titled form is checked.
        push(lang, `${prefix} ${ln}`, false, ln);
        if (fn) {
          push(lang, `${prefix} ${fn} ${ln}`, false, `${fn} ${ln}`);
          if (initial) push(lang, `${prefix} ${initial} ${ln}`, false, `${initial} ${ln}`);
        }
      }
    }
  }

  // Sans titre: first name + last name = on; initial + last = on; last alone = off.
  if (fn) {
    push('', `${fn} ${ln}`, true);
    if (initial) push('', `${initial} ${ln}`, true);
  }
  push('', ln, false);

  return out;
}
